import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { api, API } from '../lib/api';
import { cwTodayISO, cwPrettyDate } from '../lib/format';
import { ChevronLeft, ChevronRight, Loader2, LayoutGrid, CalendarDays, X, FileDown } from 'lucide-react';
import { BookingDetailDrawer } from '../components/BookingDetailDrawer';
import { StatusBadge } from '../components/StatusBadge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// ---------- date helpers (calendar-only; no tz math needed for whole days) ----------
function pad(n) { return String(n).padStart(2, '0'); }
function ymd(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }
function parseISO(s) { const [y, m, d] = s.split('-').map(Number); return { y, m, d }; }
function monthMeta(year, month) {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const startWeekday = first.getUTCDay(); // 0=Sun
  return { first, daysInMonth, startWeekday };
}
function monthLabel(year, month) {
  const names = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${names[month - 1]} ${year}`;
}
function shiftMonth(year, month, delta) {
  const idx = year * 12 + (month - 1) + delta;
  return { year: Math.floor(idx / 12), month: (idx % 12) + 1 };
}

// ---------- cell state ----------
function cellState(entry) {
  const held = entry?.held || 0;
  const confirmed = entry?.confirmed || 0;
  if (confirmed > 0) return 'confirmed';
  if (held > 0) return 'held';
  return 'free';
}
const STATE_STYLE = {
  free: { bg: '#F1F3F1', fg: '#666', label: 'Free' },
  held: { bg: '#FFE9BF', fg: '#8f5500', label: 'Held' },
  confirmed: { bg: '#B7E3B9', fg: '#1F5A24', label: 'Confirmed' },
};

// ---------- main ----------
export default function Calendar() {
  const today = cwTodayISO();
  const t = parseISO(today);
  const [year, setYear] = useState(t.y);
  const [month, setMonth] = useState(t.m);
  const [mode, setMode] = useState('grid'); // 'grid' | 'month'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [exporting, setExporting] = useState(false);
  const [selection, setSelection] = useState(null); // { date, room_type? }
  const [drawerBooking, setDrawerBooking] = useState(null);
  const { user } = useAuth();
  const isManager = user?.role === 'manager';

  const meta = useMemo(() => monthMeta(year, month), [year, month]);
  const start = ymd(year, month, 1);
  const end = ymd(year, month, meta.daysInMonth);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      const { data } = await api.get('/occupancy', { params: { start, end } });
      setData(data);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load occupancy');
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const gotoPrev = () => { const s = shiftMonth(year, month, -1); setYear(s.year); setMonth(s.month); };
  const gotoNext = () => { const s = shiftMonth(year, month, +1); setYear(s.year); setMonth(s.month); };
  const gotoToday = () => { setYear(t.y); setMonth(t.m); };

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API}/occupancy/export.csv?start=${start}&end=${end}`, {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('bb_token') || ''}` },
      });
      if (!res.ok) throw new Error(`failed (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `occupancy-${start}-to-${end}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Calendar exported');
    } catch (e) {
      toast.error('Could not export the calendar');
    } finally {
      setExporting(false);
    }
  }, [start, end]);

  const days = data?.days || [];
  const roomTypes = data?.room_types || [];
  const capacity = data?.capacity || {};

  const dayBookingsFor = (dateStr, roomType) => {
    const day = days.find((d) => d.date === dateStr);
    if (!day) return [];
    if (roomType) return day.rooms?.[roomType]?.bookings || [];
    // aggregate across rooms
    const all = [];
    for (const rt of Object.keys(day.rooms || {})) {
      for (const b of day.rooms[rt].bookings || []) all.push({ ...b, room_type: rt });
    }
    return all;
  };

  const selectionBookings = useMemo(() => {
    if (!selection) return [];
    return dayBookingsFor(selection.date, selection.room_type);
  }, [selection, data]);

  return (
    <div>
      {/* Header */}
      <div className="mb-5 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-[#1F5A24]">Occupancy calendar</h1>
          <p className="text-[#333]/70 mt-1">Held, confirmed and free nights across every room type.</p>
        </div>
        <div className="flex items-center gap-2">
          {isManager && (
            <button
              data-testid="calendar-export-csv"
              onClick={exportCsv}
              disabled={exporting || loading}
              className="inline-flex items-center gap-2 px-3 py-2 min-h-[2.5rem] rounded-lg border border-[#CFD8CF] hover:bg-[#E8F5E9] text-sm text-[#1F5A24] font-medium disabled:opacity-60"
            >
              {exporting ? <Loader2 className="animate-spin" size={14}/> : <FileDown size={14}/>}
              Export CSV
            </button>
          )}
          <div
            role="tablist"
            aria-label="Calendar view"
            className="inline-flex rounded-lg border border-[#CFD8CF] p-1 bg-[#FBFCFB]"
          >
            <button
              data-testid="calendar-view-grid"
              role="tab"
              aria-selected={mode === 'grid'}
              onClick={() => setMode('grid')}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium ${mode === 'grid' ? 'bg-[#E8F5E9] text-[#1F5A24]' : 'text-[#333]/70 hover:text-[#333]'}`}
            >
              <LayoutGrid size={14}/> Rooms
            </button>
            <button
              data-testid="calendar-view-month"
              role="tab"
              aria-selected={mode === 'month'}
              onClick={() => setMode('month')}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium ${mode === 'month' ? 'bg-[#E8F5E9] text-[#1F5A24]' : 'text-[#333]/70 hover:text-[#333]'}`}
            >
              <CalendarDays size={14}/> Month
            </button>
          </div>
        </div>
      </div>

      {/* Month navigation */}
      <div className="mb-4 flex items-center justify-between bg-[#FBFCFB] border border-[#CFD8CF] rounded-xl px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            data-testid="calendar-prev"
            onClick={gotoPrev}
            className="p-2 rounded-lg hover:bg-[#E8F5E9]"
            aria-label="Previous month"
          >
            <ChevronLeft size={18}/>
          </button>
          <button
            data-testid="calendar-next"
            onClick={gotoNext}
            className="p-2 rounded-lg hover:bg-[#E8F5E9]"
            aria-label="Next month"
          >
            <ChevronRight size={18}/>
          </button>
          <div data-testid="calendar-month-label" className="font-heading text-lg text-[#1F5A24] ml-2">
            {monthLabel(year, month)}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Legend />
          <button
            data-testid="calendar-today"
            onClick={gotoToday}
            className="px-3 py-2 rounded-lg border border-[#CFD8CF] hover:bg-[#E8F5E9] text-sm"
          >
            Today
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center gap-2 text-[#333]/70 py-8">
          <Loader2 className="animate-spin" size={16}/> Loading…
        </div>
      )}
      {err && !loading && (
        <div className="text-[#B26A00] bg-[#FFF3E0] border border-[#B26A00]/30 rounded-lg p-4">{err}</div>
      )}

      {!loading && !err && mode === 'grid' && (
        <RoomGrid
          year={year}
          month={month}
          days={days}
          roomTypes={roomTypes}
          capacity={capacity}
          today={today}
          onSelect={(date, room_type) => setSelection({ date, room_type })}
          configuredEmpty={!(data?.configured_rooms || []).length}
        />
      )}

      {!loading && !err && mode === 'month' && (
        <MonthView
          year={year}
          month={month}
          meta={meta}
          days={days}
          today={today}
          onSelect={(date) => setSelection({ date })}
        />
      )}

      {/* Side panel */}
      {selection && (
        <SidePanel
          selection={selection}
          bookings={selectionBookings}
          onClose={() => setSelection(null)}
          onOpenBooking={(bn) => setDrawerBooking(bn)}
        />
      )}
      {drawerBooking && (
        <BookingDetailDrawer bookingNumber={drawerBooking} onClose={() => setDrawerBooking(null)} />
      )}
    </div>
  );
}

// ---------- Room grid: rows = room types, cols = days ----------
function RoomGrid({ year, month, days, roomTypes, capacity, today, onSelect, configuredEmpty }) {
  const dayList = days;

  if (roomTypes.length === 0) {
    return (
      <div className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-xl p-8 text-center text-[#333]/70">
        <div className="font-medium text-[#1F5A24] mb-1">No room types yet</div>
        <p className="text-sm">Add a Rooms list in Settings to see the occupancy grid, or wait for bookings that carry a Room Type.</p>
      </div>
    );
  }

  return (
    <div className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-xl overflow-x-auto">
      {configuredEmpty && (
        <div className="text-xs text-[#8f5500] bg-[#FFF3E0] border-b border-[#B26A00]/30 px-4 py-2">
          Showing room types drawn from existing bookings. Add a Rooms list in Settings to also see capacity.
        </div>
      )}
      <table className="min-w-max text-sm border-collapse" data-testid="calendar-room-grid">
        <thead>
          <tr>
            <th className="sticky left-0 z-10 bg-[#FBFCFB] border-b border-r border-[#CFD8CF] px-3 py-2 text-left font-medium text-[#333]/70 min-w-[180px]">
              Room type
            </th>
            {dayList.map((d) => {
              const dt = parseISO(d.date);
              const isToday = d.date === today;
              const wd = new Date(Date.UTC(dt.y, dt.m - 1, dt.d)).getUTCDay();
              const isWeekend = wd === 0 || wd === 6;
              return (
                <th
                  key={d.date}
                  className={`border-b border-[#CFD8CF] px-2 py-2 font-medium ${isWeekend ? 'bg-[#F5F1E8]' : ''} ${isToday ? 'text-[#1F5A24]' : 'text-[#333]/70'}`}
                >
                  <div className="text-[10px] uppercase tracking-widest">
                    {['S','M','T','W','T','F','S'][wd]}
                  </div>
                  <div className={`text-sm ${isToday ? 'font-bold' : ''}`}>{dt.d}</div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {roomTypes.map((rt) => {
            const cap = capacity[rt] ?? 0;
            return (
              <tr key={rt}>
                <th className="sticky left-0 z-10 bg-[#FBFCFB] border-b border-r border-[#CFD8CF] px-3 py-2 text-left font-medium text-[#1F5A24] whitespace-nowrap">
                  <div>{rt}</div>
                  <div className="text-[11px] font-normal text-[#333]/60">
                    {cap > 0 ? `capacity ${cap}` : 'no capacity set'}
                  </div>
                </th>
                {dayList.map((d) => {
                  const entry = d.rooms?.[rt] || { held: 0, confirmed: 0, bookings: [] };
                  const state = cellState(entry);
                  const style = STATE_STYLE[state];
                  const occupied = entry.held + entry.confirmed;
                  const isToday = d.date === today;
                  const label = cap > 0 ? `${occupied}/${cap}` : (occupied > 0 ? String(occupied) : '');
                  return (
                    <td
                      key={d.date + rt}
                      className={`border-b border-[#CFD8CF] p-1 text-center align-middle`}
                    >
                      <button
                        data-testid={`cell-${rt}-${d.date}`}
                        onClick={() => onSelect(d.date, rt)}
                        title={`${rt} · ${cwPrettyDate(d.date)} · Held ${entry.held}, Confirmed ${entry.confirmed}${cap ? `, Capacity ${cap}` : ''}`}
                        className={`w-10 h-9 rounded-md font-medium text-xs transition-shadow hover:shadow-sm ${isToday ? 'ring-2 ring-[#2E7D32]' : ''}`}
                        style={{ background: style.bg, color: style.fg }}
                      >
                        {label || <span className="opacity-40">·</span>}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Month view: 7-col traditional grid ----------
function MonthView({ year, month, meta, days, today, onSelect }) {
  // Build 6 rows x 7 cols
  const cells = [];
  const lead = meta.startWeekday;
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= meta.daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const totalsFor = (dateStr) => {
    const day = days.find((x) => x.date === dateStr);
    if (!day) return { held: 0, confirmed: 0 };
    let held = 0, confirmed = 0;
    for (const rt of Object.keys(day.rooms || {})) {
      held += day.rooms[rt].held || 0;
      confirmed += day.rooms[rt].confirmed || 0;
    }
    return { held, confirmed };
  };

  return (
    <div className="bg-[#FBFCFB] border border-[#CFD8CF] rounded-xl overflow-hidden" data-testid="calendar-month-grid">
      <div className="grid grid-cols-7 border-b border-[#CFD8CF] bg-[#F5F1E8]">
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map((d, i) => (
          <div key={d} className="text-[11px] uppercase tracking-widest text-[#333]/60 font-medium px-3 py-2 text-center">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((day, idx) => {
          if (!day) {
            return <div key={idx} className="border-t border-l border-[#CFD8CF] bg-[#FBFCFB]/40 min-h-[90px]" />;
          }
          const dateStr = ymd(year, month, day);
          const { held, confirmed } = totalsFor(dateStr);
          const state = cellState({ held, confirmed });
          const style = STATE_STYLE[state];
          const isToday = dateStr === today;
          const col = idx % 7;
          return (
            <button
              key={idx}
              data-testid={`month-cell-${dateStr}`}
              onClick={() => onSelect(dateStr)}
              className={`text-left border-t ${col === 0 ? '' : 'border-l'} border-[#CFD8CF] min-h-[90px] p-2 hover:bg-[#E8F5E9]/60 transition-colors ${isToday ? 'ring-2 ring-inset ring-[#2E7D32]' : ''}`}
            >
              <div className="flex items-start justify-between">
                <span className={`text-sm ${isToday ? 'font-bold text-[#1F5A24]' : 'text-[#333]'}`}>{day}</span>
                <span
                  className="inline-block w-2.5 h-2.5 rounded-full"
                  style={{ background: style.bg, border: `1px solid ${style.fg}33` }}
                  aria-hidden
                />
              </div>
              <div className="mt-2 space-y-1 text-[11px]">
                {confirmed > 0 && (
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: STATE_STYLE.confirmed.bg, color: STATE_STYLE.confirmed.fg }}>
                    <span className="font-semibold">{confirmed}</span> confirmed
                  </div>
                )}
                {held > 0 && (
                  <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: STATE_STYLE.held.bg, color: STATE_STYLE.held.fg }}>
                    <span className="font-semibold">{held}</span> held
                  </div>
                )}
                {held === 0 && confirmed === 0 && (
                  <div className="text-[#333]/40">—</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Legend ----------
function Legend() {
  return (
    <div className="hidden md:flex items-center gap-3 text-xs text-[#333]/70">
      {['confirmed','held','free'].map((k) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className="w-3 h-3 rounded" style={{ background: STATE_STYLE[k].bg, border: `1px solid ${STATE_STYLE[k].fg}33` }} aria-hidden/>
          {STATE_STYLE[k].label}
        </span>
      ))}
    </div>
  );
}

// ---------- Side panel ----------
function SidePanel({ selection, bookings, onClose, onOpenBooking }) {
  return (
    <div className="fixed inset-0 z-40" data-testid="calendar-side-panel">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[440px] bg-[#FBFCFB] border-l border-[#CFD8CF] overflow-y-auto animate-slide-in-right">
        <div className="sticky top-0 bg-[#FBFCFB] border-b border-[#CFD8CF] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-[#333]/60">
              {selection.room_type || 'All rooms'}
            </div>
            <div className="font-heading text-lg text-[#1F5A24]">{cwPrettyDate(selection.date)}</div>
          </div>
          <button data-testid="side-panel-close" onClick={onClose} className="p-2 rounded-lg hover:bg-[#E8F5E9]">
            <X size={20}/>
          </button>
        </div>
        <div className="p-5">
          {bookings.length === 0 ? (
            <div className="text-[#333]/60 text-sm">No bookings on this night. Room is free.</div>
          ) : (
            <ul className="space-y-2">
              {bookings.map((b) => (
                <li key={b.booking_number}>
                  <button
                    data-testid={`side-panel-booking-${b.booking_number}`}
                    onClick={() => onOpenBooking(b.booking_number)}
                    className="w-full text-left bg-white border border-[#CFD8CF] rounded-xl p-3 hover:border-[#2E7D32] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <div className="font-medium truncate">{b.name}</div>
                      <span className="text-xs text-[#333]/60 ml-auto shrink-0">{b.booking_number}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      <StatusBadge status={b.status} checkedIn={b.checked_in && b.status === 'Confirmed'} />
                      {b.room_type && (
                        <span className="text-xs text-[#333]/70">{b.room_type}</span>
                      )}
                      <span className="text-xs text-[#333]/60">
                        {cwPrettyDate(b.check_in)} → {cwPrettyDate(b.check_out)}
                      </span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
